import { parseGeometryEpochs, type GeometryEpochs, type GeometryKey } from '@vitals/shared';

export type GeometryEpochsListener = (epochs: GeometryEpochs) => void;

/** Read side of the hub: what geometry loaders depend on. */
export interface GeometryEpochSource {
  get(key: GeometryKey): string | undefined;
  onChange(cb: GeometryEpochsListener): () => void;
}

/** Write side: what the WS client feeds raw, untrusted `geometry` fields into. */
export interface GeometryEpochSink {
  announce(raw: unknown): void;
}

/**
 * The latest drawn-geometry epochs the server has announced over the WS.
 * Announcements are validated here (the system boundary) and MERGED: a key
 * missing from a later announcement keeps its previous epoch, because a fresh
 * deploy that hasn't finished loading e.g. LIRR yet says nothing about it — the
 * tab should keep its current LIRR lines until the new build announces them.
 */
export class GeometryEpochHub implements GeometryEpochSource, GeometryEpochSink {
  private epochs: GeometryEpochs = {};
  private listeners = new Set<GeometryEpochsListener>();

  announce(raw: unknown): void {
    const parsed = parseGeometryEpochs(raw);
    if (parsed === null) return;
    const next: GeometryEpochs = { ...this.epochs, ...parsed };
    const changed = (Object.keys(next) as GeometryKey[]).some((key) => next[key] !== this.epochs[key]);
    if (!changed) return;
    this.epochs = next;
    for (const cb of this.listeners) cb(next);
  }

  get(key: GeometryKey): string | undefined {
    return this.epochs[key];
  }

  onChange(cb: GeometryEpochsListener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }
}
